import styles from './adminPage.module.css';
import { adminAPI, downloadCSV } from './utils/adminAPI';
import ToolModal from './components/ToolModal';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export const MainSectionAdmin = () => {

    const [stats, setStats] = useState({
        totalUsers: 0,
        totalTrips: 0,
        totalExpenses: 0,
        totalMessages: 0,
        activeUsers: 0,
        totalExpenseAmount: 0,
    });
    const [recentUsers, setRecentUsers] = useState([]);
    const [recentTrips, setRecentTrips] = useState([]);
    const [recentMessages, setRecentMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedSection, setExpandedSection] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalTool, setModalTool] = useState(null);
    const [toolLoading, setToolLoading] = useState(false);
    const [toolMessage, setToolMessage] = useState('');

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);

            const [usersData, tripsData, expensesData, messagesData] = await Promise.all([
                adminAPI.users().catch(e => { console.error(e); return { users: [] }; }),
                adminAPI.trips().catch(e => { console.error(e); return { trips: [] }; }),
                adminAPI.expenses().catch(e => { console.error(e); return { expenses: [] }; }),
                adminAPI.messages().catch(e => { console.error(e); return { messages: [] }; }),
            ]);

            const allUsers = usersData.users || [];
            const allTrips = tripsData.trips || [];
            const allExpenses = expensesData.expenses || [];
            const allMessages = messagesData.messages || [];

            const totalExpenseAmount = allExpenses.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);
            const activeUsers = allUsers.filter(u => u.is_active).length;

            setStats({
                totalUsers: allUsers.length,
                totalTrips: allTrips.length,
                totalExpenses: allExpenses.length,
                totalMessages: allMessages.length,
                activeUsers,
                totalExpenseAmount,
            });

            setRecentUsers(allUsers.slice(0, 5));
            setRecentTrips(allTrips.slice(0, 5));
            setRecentMessages(allMessages.slice(0, 5));
        } catch (err) {
            console.error("Error fetching dashboard data:", err);
            setError('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const handleToolClick = (toolName) => {
        setModalTool(toolName);
        setModalOpen(true);
    };

    const executeTool = async (toolName, params = {}) => {
        setToolLoading(true);
        setToolMessage('');

        try {
            let result;

            switch (toolName) {
                // ── User Management ──────────────────
                case 'Bulk Operations': {
                    result = await adminAPI.bulkOperation(params.action, params.userIds);
                    setToolMessage(result.message);
                    break;
                }
                case 'Export Users': {
                    const usersBlob = await adminAPI.exportUsers();
                    downloadCSV(usersBlob, 'users_export.csv');
                    setToolMessage('Users exported successfully');
                    break;
                }
                case 'Export Trips': {
                    const tripsBlob = await adminAPI.exportTrips();
                    downloadCSV(tripsBlob, 'trips_export.csv');
                    setToolMessage('Trips exported successfully');
                    break;
                }
                case 'Import Users': {
                    setToolMessage('Import feature: Export a CSV, edit it, then use the Bulk Operations tool to re-import.');
                    break;
                }
                case 'Mass Email': {
                    result = await adminAPI.massEmail(params.userIds, params.subject, params.body);
                    setToolMessage(result.message);
                    break;
                }
                case 'User Roles': {
                    result = await adminAPI.admins();
                    setToolMessage(`Found ${result.admins?.length || 0} admin user(s). Use Promote/Demote to change roles.`);
                    break;
                }
                case 'Activity Logs': {
                    result = await adminAPI.activityLogs(params.userId);
                    setToolMessage(`User: ${result.user?.firstname} ${result.user?.lastname} — ${result.summary?.totalTrips} trips, ${result.summary?.totalExpenses} expenses, $${result.summary?.totalSpent?.toFixed(2)} spent`);
                    break;
                }

                // ── Reports & Analytics ──────────────
                case 'User Growth Report': {
                    result = await adminAPI.userGrowth();
                    setToolMessage(`User growth data retrieved: ${result.data?.length || 0} months of data`);
                    break;
                }
                case 'Revenue Analytics': {
                    result = await adminAPI.revenue();
                    setToolMessage(`Revenue analytics retrieved: ${result.data?.length || 0} months of data`);
                    break;
                }
                case 'Trip Statistics': {
                    result = await adminAPI.tripStatistics();
                    const d = result.data;
                    setToolMessage(`Total trips: ${d?.total_trips}, Avg budget: $${d?.avg_budget?.toFixed(2) || 0}, Top destinations: ${result.topDestinations?.map(t => t.destination).join(', ') || 'N/A'}`);
                    break;
                }
                case 'Expense Breakdown': {
                    result = await adminAPI.expenseBreakdown();
                    const categories = result.data?.map(c => `${c.category}: $${c.total?.toFixed(0)}`).join(', ');
                    setToolMessage(`Expense breakdown: ${categories || 'No data'}`);
                    break;
                }
                case 'Engagement Metrics': {
                    result = await adminAPI.engagement();
                    const d2 = result.data;
                    setToolMessage(`Total users: ${d2?.totalUsers}, Users with trips: ${d2?.users_with_trips}, Active 30d: ${d2?.active_last_30_days}, Active 7d: ${d2?.active_last_7_days}`);
                    break;
                }
                case 'Report Builder': {
                    setToolMessage('Use the Analytics Dashboard (/admin/analytics) for detailed charts and reports.');
                    break;
                }

                // ── System Management ────────────────
                case 'System Health': {
                    result = await adminAPI.health();
                    setToolMessage(`Status: ${result.status} | DB: ${result.database?.name} (${result.database?.size}) | Uptime: ${result.server?.uptimeFormatted} | Memory: ${result.server?.memoryUsage?.heapUsed}`);
                    break;
                }
                case 'Server Status': {
                    result = await adminAPI.health();
                    setToolMessage(`Server: ${result.status} | Node ${result.server?.nodeVersion} | Platform: ${result.server?.platform} | Query: ${result.database?.queryTime}`);
                    break;
                }
                case 'Database Stats': {
                    result = await adminAPI.dbStats();
                    setToolMessage(`Users: ${result.users} | Trips: ${result.trips} | Expenses: ${result.expenses} | Messages: ${result.messages}`);
                    break;
                }
                case 'Backup Database': {
                    result = await adminAPI.backup();
                    setToolMessage(result.message + ` — ${JSON.stringify(result.backup?.tables)}`);
                    break;
                }
                case 'Restore Backup': {
                    setToolMessage('Use pg_restore with your PostgreSQL backup file to restore data.');
                    break;
                }
                case 'Clear Cache': {
                    result = await adminAPI.clearCache();
                    setToolMessage(result.message);
                    break;
                }
                case 'Optimize DB': {
                    result = await adminAPI.optimizeDB();
                    setToolMessage(result.message);
                    break;
                }

                // ── Security & Access ────────────────
                case 'Admin Management': {
                    result = await adminAPI.admins();
                    const adminNames = result.admins?.map(a => `${a.firstname} ${a.lastname} (${a.email})`).join(', ');
                    setToolMessage(`${result.total || 0} admin(s): ${adminNames || 'None'}`);
                    break;
                }
                case 'Login History': {
                    result = await adminAPI.loginHistory();
                    setToolMessage(`${result.recentRegistrations?.length || 0} recent registrations, ${result.securityEvents?.length || 0} security events`);
                    break;
                }
                case 'API Keys': {
                    setToolMessage('API keys are managed via environment variables. Contact your DevOps team for changes.');
                    break;
                }
                case 'Security Settings': {
                    result = await adminAPI.getSettings();
                    setToolMessage(`${Object.keys(result.settings || {}).length} setting(s) configured`);
                    break;
                }
                case 'Audit Logs': {
                    result = await adminAPI.auditLogs();
                    const recentActions = result.logs?.slice(0, 5).map(l => `${l.action} by ${l.firstname || 'system'}`).join(', ');
                    setToolMessage(`${result.total || 0} total audit entries. Recent: ${recentActions || 'None'}`);
                    break;
                }
                case 'Suspend Users': {
                    result = await adminAPI.suspendUser(params.userId);
                    setToolMessage(result.message);
                    break;
                }

                // ── Communication ────────────────────
                case 'Email Templates': {
                    setToolMessage('Use the Email Campaign Studio (/admin/email) for template management.');
                    break;
                }
                case 'SMS Settings': {
                    setToolMessage('SMS integration is pending setup. Configure via Admin Settings.');
                    break;
                }
                case 'Push Notifications': {
                    result = await adminAPI.sendNotification(params.userIds, params.title, params.message);
                    setToolMessage(result.message);
                    break;
                }
                case 'Scheduled Messages': {
                    result = await adminAPI.getScheduledTasks();
                    setToolMessage(`${result.tasks?.length || 0} scheduled task(s) found`);
                    break;
                }
                case 'Announcements': {
                    if (params.title && params.content) {
                        result = await adminAPI.createAnnouncement(params.title, params.content, params.type || 'info');
                        setToolMessage(result.message);
                    } else {
                        result = await adminAPI.getAnnouncements();
                        setToolMessage(`${result.announcements?.length || 0} announcement(s) found`);
                    }
                    break;
                }
                case 'Newsletter': {
                    setToolMessage('Use the Email Campaign Studio (/admin/email) for newsletter campaigns.');
                    break;
                }

                // ── Content Management ───────────────
                case 'FAQs': {
                    result = await adminAPI.getSettings();
                    setToolMessage('FAQ management: Update via Settings. Key: "faqs"');
                    break;
                }
                case 'Blog': {
                    setToolMessage('Blog management: Configure in Settings → Content section.');
                    break;
                }
                case 'Terms & Policies': {
                    setToolMessage('Terms & policies: Update via Settings. Key: "terms_and_policies"');
                    break;
                }
                case 'Help Docs': {
                    setToolMessage('Help documentation: Manage via Settings. Key: "help_docs"');
                    break;
                }
                case 'Maintenance': {
                    if (params.enabled !== undefined) {
                        result = await adminAPI.setMaintenanceMode(params.enabled, params.message || '');
                        setToolMessage(result.message);
                    } else {
                        result = await adminAPI.getMaintenanceStatus();
                        setToolMessage(`Maintenance mode: ${result.enabled ? 'ON' : 'OFF'}${result.message ? ` — "${result.message}"` : ''}`);
                    }
                    break;
                }
                case 'Widgets': {
                    setToolMessage('Widget management: Configure layouts in Settings. Key: "widgets"');
                    break;
                }

                // ── Data Management ──────────────────
                case 'Validate Data': {
                    result = await adminAPI.validateData();
                    const issueList = result.issues?.map(i => `${i.type}: ${i.message}`).join(' | ');
                    setToolMessage(`Status: ${result.status} — ${result.totalIssues || 0} issue(s).${issueList ? ' ' + issueList : ''}`);
                    break;
                }
                case 'Find Duplicates': {
                    result = await adminAPI.findDuplicates();
                    setToolMessage(`Email duplicates: ${result.totalEmailDuplicates || 0}, Trip duplicates: ${result.totalTripDuplicates || 0}`);
                    break;
                }
                case 'Data Migration': {
                    setToolMessage('Data migration: Use Export tools to download data, then import into target system.');
                    break;
                }
                case 'Archive Data': {
                    result = await adminAPI.archive(params.yearsOld || 1);
                    setToolMessage(`${result.message}. Trips: ${result.recordsIdentified?.trips || 0}, Expenses: ${result.recordsIdentified?.expenses || 0}`);
                    break;
                }
                case 'Cleanup': {
                    result = await adminAPI.cleanupInactive(params.months || 6);
                    setToolMessage(result.message);
                    break;
                }
                case 'Data Recovery': {
                    result = await adminAPI.recoverData();
                    const fixes = result.fixes?.length > 0 ? result.fixes.join(', ') : 'No issues to fix';
                    setToolMessage(`${result.message}. ${fixes}`);
                    break;
                }

                // ── Integrations ─────────────────────
                case 'Third-party Apps': {
                    result = await adminAPI.integrationsStatus();
                    const statuses = Object.entries(result.integrations || {}).map(([k, v]) => `${k}: ${v.status}`).join(', ');
                    setToolMessage(`Integration statuses: ${statuses}`);
                    break;
                }
                case 'Webhooks': {
                    setToolMessage('Webhook management: Configure in Settings. Key: "webhooks"');
                    break;
                }
                case 'API Docs': {
                    setToolMessage('API documentation is available at /health on the admin backend (port 5002)');
                    break;
                }
                case 'Payment Gateway': {
                    setToolMessage('Payment gateway: Not yet configured. Update Settings when ready.');
                    break;
                }
                case 'Email Service': {
                    setToolMessage('Email service (Resend) is configured via the main backend. Use Email Campaign Studio for sending.');
                    break;
                }
                case 'Analytics': {
                    result = await adminAPI.integrationsStatus();
                    setToolMessage('Analytics integration active. Visit /admin/analytics for the full dashboard.');
                    break;
                }

                // ── Customization ────────────────────
                case 'Theme Settings': {
                    setToolMessage('Theme settings: Update via Settings. Key: "theme"');
                    break;
                }
                case 'Custom CSS': {
                    setToolMessage('Custom CSS: Update via Settings. Key: "custom_css"');
                    break;
                }
                case 'Branding': {
                    setToolMessage('Branding: Update logo and colors in Settings. Key: "branding"');
                    break;
                }
                case 'Page Templates': {
                    setToolMessage('Page templates: Manage via Email Campaign Studio (/admin/email).');
                    break;
                }
                case 'Custom Fields': {
                    setToolMessage('Custom fields: Configure in Settings. Key: "custom_fields"');
                    break;
                }
                case 'Layouts': {
                    setToolMessage('Layout customization: Update via Settings. Key: "layouts"');
                    break;
                }

                default: {
                    setToolMessage('Tool not implemented yet');
                }
            }

            // Auto-refresh dashboard
            setTimeout(() => fetchDashboardData(), 1000);
        } catch (err) {
            console.error('Tool error:', err);
            setToolMessage(`Error: ${err.message}`);
        } finally {
            setToolLoading(false);
        }
    };

    return (
        <main className={styles.adminMain}>
            <ToolModal
                isOpen={modalOpen}
                toolName={modalTool}
                onClose={() => {
                    setModalOpen(false);
                    setToolMessage('');
                }}
                onExecute={executeTool}
                isLoading={toolLoading}
                message={toolMessage}
            />
            {error && <div className={styles.errorAlert}>{error}</div>}

            {loading ? (
                <div className={styles.loadingContainer}>
                    <p>Loading dashboard...</p>
                </div>
            ) : (
                <>
                    {/* Stats Grid */}
                    <section className={styles.statsSection}>
                        <h2>Key Metrics</h2>
                        <div className={styles.statsGrid}>
                            <div className={styles.statCard}>
                                <div className={styles.statIcon}>👥</div>
                                <div className={styles.statContent}>
                                    <div className={styles.statNumber}>{stats.totalUsers}</div>
                                    <div className={styles.statLabel}>Total Users</div>
                                    <div className={styles.statSubtext}>{stats.activeUsers} active</div>
                                </div>
                                <Link to="/admin/users" className={styles.statLink}>View →</Link>
                            </div>

                            <div className={styles.statCard}>
                                <div className={styles.statIcon}>✈️</div>
                                <div className={styles.statContent}>
                                    <div className={styles.statNumber}>{stats.totalTrips}</div>
                                    <div className={styles.statLabel}>Total Trips</div>
                                    <div className={styles.statSubtext}>Managed trips</div>
                                </div>
                                <Link to="/admin/trips" className={styles.statLink}>View →</Link>
                            </div>

                            <div className={styles.statCard}>
                                <div className={styles.statIcon}>💰</div>
                                <div className={styles.statContent}>
                                    <div className={styles.statNumber}>${stats.totalExpenseAmount.toFixed(0)}</div>
                                    <div className={styles.statLabel}>Total Expenses</div>
                                    <div className={styles.statSubtext}>{stats.totalExpenses} transactions</div>
                                </div>
                                <Link to="/admin/expenses" className={styles.statLink}>View →</Link>
                            </div>

                            <div className={styles.statCard}>
                                <div className={styles.statIcon}>💬</div>
                                <div className={styles.statContent}>
                                    <div className={styles.statNumber}>{stats.totalMessages}</div>
                                    <div className={styles.statLabel}>Messages</div>
                                    <div className={styles.statSubtext}>User inquiries</div>
                                </div>
                                <Link to="/admin/messages" className={styles.statLink}>View →</Link>
                            </div>
                        </div>
                    </section>

                    {/* Recent Users Section */}
                    <section className={styles.contentSection}>
                        <div className={styles.sectionHeader}>
                            <h3>Recent Users</h3>
                            <Link to="/admin/users" className={styles.viewAllLink}>View All →</Link>
                        </div>
                        {recentUsers.length === 0 ? (
                            <p className={styles.emptyMessage}>No users yet.</p>
                        ) : (
                            <div className={styles.listContainer}>
                                {recentUsers.map(user => (
                                    <div key={user.id} className={styles.listItem}>
                                        <div className={styles.itemHeader}>
                                            <div>
                                                <strong>{user.firstname} {user.lastname}</strong>
                                                <span className={styles.itemSubtext}>{user.email}</span>
                                            </div>
                                            <span className={`${styles.badge} ${user.is_active ? styles.badgeActive : styles.badgeInactive}`}>
                                                {user.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        <div className={styles.itemMeta}>
                                            <span>ID: {user.id}</span>
                                            <span>Role: {user.role}</span>
                                            <Link to={`/admin/users/${user.id}`} className={styles.quickLink}>View Profile</Link>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    {/* Recent Trips Section */}
                    <section className={styles.contentSection}>
                        <div className={styles.sectionHeader}>
                            <h3>Recent Trips</h3>
                            <Link to="/admin/trips" className={styles.viewAllLink}>View All →</Link>
                        </div>
                        {recentTrips.length === 0 ? (
                            <p className={styles.emptyMessage}>No trips created yet.</p>
                        ) : (
                            <div className={styles.listContainer}>
                                {recentTrips.map(trip => (
                                    <div key={trip.id} className={styles.listItem}>
                                        <div className={styles.itemHeader}>
                                            <div>
                                                <strong>{trip.trip_name}</strong>
                                                <span className={styles.itemSubtext}>{trip.destination}</span>
                                            </div>
                                            <span className={styles.badgeBudget}>
                                                ${trip.budget}
                                            </span>
                                        </div>
                                        <div className={styles.itemMeta}>
                                            <span>📅 {new Date(trip.start_date).toLocaleDateString()} - {new Date(trip.end_date).toLocaleDateString()}</span>
                                            <span>Trip ID: {trip.id}</span>
                                            <Link to="/admin/trips" className={styles.quickLink}>View Trip</Link>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    {/* Recent Messages Section */}
                    <section className={styles.contentSection}>
                        <div className={styles.sectionHeader}>
                            <h3>Recent Messages</h3>
                            <Link to="/admin/messages" className={styles.viewAllLink}>View All →</Link>
                        </div>
                        {recentMessages.length === 0 ? (
                            <p className={styles.emptyMessage}>No messages received yet.</p>
                        ) : (
                            <div className={styles.listContainer}>
                                {recentMessages.map(msg => (
                                    <div key={msg.id} className={styles.listItem}>
                                        <div className={styles.itemHeader}>
                                            <div>
                                                <strong>{msg.name}</strong>
                                                <span className={styles.itemSubtext}>{msg.email}</span>
                                            </div>
                                            <span className={styles.messageTime}>
                                                {new Date(msg.created_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <div className={styles.messagePreview}>
                                            {msg.message?.substring(0, 100)}...
                                        </div>
                                        <Link to="/admin/messages" className={styles.quickLink}>View Message</Link>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    {/* Quick Actions */}
                    <section className={styles.quickActionsSection}>
                        <h3>Quick Actions</h3>
                        <div className={styles.actionsGrid}>
                            <Link to="/admin/users" className={styles.actionCard}>
                                <span className={styles.actionIcon}>👥</span>
                                <span className={styles.actionLabel}>Manage Users</span>
                            </Link>
                            <Link to="/admin/trips" className={styles.actionCard}>
                                <span className={styles.actionIcon}>✈️</span>
                                <span className={styles.actionLabel}>Manage Trips</span>
                            </Link>
                            <Link to="/admin/expenses" className={styles.actionCard}>
                                <span className={styles.actionIcon}>💰</span>
                                <span className={styles.actionLabel}>Manage Expenses</span>
                            </Link>
                            <Link to="/admin/messages" className={styles.actionCard}>
                                <span className={styles.actionIcon}>💬</span>
                                <span className={styles.actionLabel}>View Messages</span>
                            </Link>
                            <Link to="/admin/tags" className={styles.actionCard}>
                                <span className={styles.actionIcon}>🏷️</span>
                                <span className={styles.actionLabel}>Manage Tags</span>
                            </Link>
                            <button className={styles.actionCard} onClick={fetchDashboardData}>
                                <span className={styles.actionIcon}>🔄</span>
                                <span className={styles.actionLabel}>Refresh Data</span>
                            </button>
                        </div>
                    </section>

                    {/* Advanced Admin Tools Section */}
                    <section className={styles.advancedSection}>
                        <div className={styles.sectionHeader}>
                            <h3>Advanced Admin Tools</h3>
                            <button
                                className={styles.expandBtn}
                                onClick={() => setExpandedSection(expandedSection === 'advanced' ? null : 'advanced')}
                            >
                                {expandedSection === 'advanced' ? '−' : '+'}
                            </button>
                        </div>

                        {expandedSection === 'advanced' && (
                            <div className={styles.toolsGrid}>
                                {/* User Management Tools */}
                                <div className={styles.toolCategory}>
                                    <h4>👥 User Management</h4>
                                    <div className={styles.toolsList}>
                                        <button className={styles.toolBtn} onClick={() => handleToolClick('Bulk Operations')} title="Bulk operations on users">
                                            📦 Bulk Operations
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Export Users')} title="Export user data to CSV">
                                            📥 Export Users
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Export Trips')} title="Export trip data to CSV">
                                            📥 Export Trips
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => handleToolClick('Mass Email')} title="Send mass email to users">
                                            📧 Mass Email
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('User Roles')} title="Manage user roles and permissions">
                                            🔐 User Roles
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => handleToolClick('Activity Logs')} title="View user activity logs">
                                            📊 Activity Logs
                                        </button>
                                    </div>
                                </div>

                                {/* Reports & Analytics */}
                                <div className={styles.toolCategory}>
                                    <h4>📈 Reports & Analytics</h4>
                                    <div className={styles.toolsList}>
                                        <button className={styles.toolBtn} onClick={() => executeTool('User Growth Report')} title="Generate user growth report">
                                            📊 User Growth Report
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Revenue Analytics')} title="Revenue analytics">
                                            💹 Revenue Analytics
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Trip Statistics')} title="Trip statistics">
                                            ✈️ Trip Statistics
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Expense Breakdown')} title="Expense breakdown">
                                            💸 Expense Breakdown
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Engagement Metrics')} title="Engagement metrics">
                                            🎯 Engagement Metrics
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Report Builder')} title="Custom report builder">
                                            🛠️ Report Builder
                                        </button>
                                    </div>
                                </div>

                                {/* System Management */}
                                <div className={styles.toolCategory}>
                                    <h4>⚙️ System Management</h4>
                                    <div className={styles.toolsList}>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Backup Database')} title="Database backup">
                                            💾 Backup Database
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Restore Backup')} title="Restore from backup">
                                            ↩️ Restore Backup
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('System Health')} title="View system health">
                                            🏥 System Health
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Server Status')} title="Server status monitoring">
                                            📡 Server Status
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Clear Cache')} title="Clear cache">
                                            🗑️ Clear Cache
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Optimize DB')} title="Database optimization">
                                            ⚡ Optimize DB
                                        </button>
                                    </div>
                                </div>

                                {/* Security & Access */}
                                <div className={styles.toolCategory}>
                                    <h4>🔒 Security & Access</h4>
                                    <div className={styles.toolsList}>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Admin Management')} title="Manage admin users">
                                            👮 Admin Management
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Login History')} title="View login history">
                                            🔐 Login History
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Audit Logs')} title="View audit logs">
                                            📋 Audit Logs
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Security Settings')} title="Security settings">
                                            🛡️ Security Settings
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => handleToolClick('Suspend Users')} title="Suspend user accounts">
                                            ⛔ Suspend Users
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('API Keys')} title="Manage API keys">
                                            🔑 API Keys
                                        </button>
                                    </div>
                                </div>

                                {/* Communication Tools */}
                                <div className={styles.toolCategory}>
                                    <h4>💬 Communication</h4>
                                    <div className={styles.toolsList}>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Email Templates')} title="Email templates">
                                            📧 Email Templates
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => handleToolClick('Push Notifications')} title="Push notifications">
                                            🔔 Push Notifications
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Scheduled Messages')} title="Scheduled messages">
                                            ⏰ Scheduled Messages
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => handleToolClick('Announcements')} title="Announcements">
                                            📢 Announcements
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Newsletter')} title="Newsletter">
                                            📰 Newsletter
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('SMS Settings')} title="SMS notifications">
                                            📱 SMS Settings
                                        </button>
                                    </div>
                                </div>

                                {/* Content Management */}
                                <div className={styles.toolCategory}>
                                    <h4>📝 Content Management</h4>
                                    <div className={styles.toolsList}>
                                        <button className={styles.toolBtn} onClick={() => executeTool('FAQs')} title="Manage FAQs">
                                            ❓ FAQs
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Blog')} title="Blog posts">
                                            📚 Blog
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Terms & Policies')} title="Terms and policies">
                                            ⚖️ Terms & Policies
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Help Docs')} title="Help documentation">
                                            📖 Help Docs
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Maintenance')} title="Site maintenance">
                                            🚧 Maintenance
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Widgets')} title="Widget management">
                                            🎨 Widgets
                                        </button>
                                    </div>
                                </div>

                                {/* Data Management */}
                                <div className={styles.toolCategory}>
                                    <h4>🗃️ Data Management</h4>
                                    <div className={styles.toolsList}>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Validate Data')} title="Data validation">
                                            ✔️ Validate Data
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Find Duplicates')} title="Duplicate finder">
                                            🔍 Find Duplicates
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Data Recovery')} title="Data recovery">
                                            🔙 Data Recovery
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => handleToolClick('Archive Data')} title="Archive old data">
                                            📦 Archive Data
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => handleToolClick('Cleanup')} title="Delete inactive users">
                                            🗑️ Cleanup
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Data Migration')} title="Data migration">
                                            🔄 Data Migration
                                        </button>
                                    </div>
                                </div>

                                {/* Integration & API */}
                                <div className={styles.toolCategory}>
                                    <h4>🔗 Integrations</h4>
                                    <div className={styles.toolsList}>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Third-party Apps')} title="Third-party integrations">
                                            🔌 Third-party Apps
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Webhooks')} title="Webhook management">
                                            🪝 Webhooks
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('API Docs')} title="API documentation">
                                            📚 API Docs
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Payment Gateway')} title="Payment gateway">
                                            💳 Payment Gateway
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Email Service')} title="Email service">
                                            📬 Email Service
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Analytics')} title="Analytics integration">
                                            📊 Analytics
                                        </button>
                                    </div>
                                </div>

                                {/* Customization */}
                                <div className={styles.toolCategory}>
                                    <h4>🎨 Customization</h4>
                                    <div className={styles.toolsList}>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Theme Settings')} title="Theme settings">
                                            🎭 Theme Settings
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Custom CSS')} title="Custom CSS">
                                            💻 Custom CSS
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Branding')} title="Logo and branding">
                                            🏷️ Branding
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Page Templates')} title="Page templates">
                                            📄 Page Templates
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Custom Fields')} title="Custom fields">
                                            ➕ Custom Fields
                                        </button>
                                        <button className={styles.toolBtn} onClick={() => executeTool('Layouts')} title="Layout customization">
                                            📐 Layouts
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>

                    {/* System Info Footer */}
                    <section className={styles.systemInfoSection}>
                        <h3>System Information</h3>
                        <div className={styles.systemGrid}>
                            <div className={styles.systemItem}>
                                <span className={styles.systemLabel}>Last Updated:</span>
                                <span className={styles.systemValue}>{new Date().toLocaleString()}</span>
                            </div>
                            <div className={styles.systemItem}>
                                <span className={styles.systemLabel}>Active Sessions:</span>
                                <span className={styles.systemValue}>1</span>
                            </div>
                            <div className={styles.systemItem}>
                                <span className={styles.systemLabel}>Database Status:</span>
                                <span className={`${styles.systemValue} ${styles.statusOk}`}>Connected</span>
                            </div>
                            <div className={styles.systemItem}>
                                <span className={styles.systemLabel}>API Status:</span>
                                <span className={`${styles.systemValue} ${styles.statusOk}`}>Operational</span>
                            </div>
                        </div>
                    </section>
                </>
            )}
        </main>
    );
};
